/* Scoped adapter for the supplied Minimog cart.js/cart-drawer.js.
   Actual Shopify lines and totals remain intact; only this form's linked groups are presented together. */
(() => {
  'use strict';
  const groupId = item => String(item.properties?._Configuration || '');
  const parentKey = item => item.parent_relationship?.parent_key || '';
  function members(cart, id) {
    const parents = cart.items.filter(item => groupId(item) === id && !parentKey(item));
    if (!id || parents.length !== 1) throw new Error('This configuration has changed. Refresh the cart and try again.');
    const parent = parents[0];
    return [parent, ...cart.items.filter(item => groupId(item) === id && parentKey(item) === parent.key)];
  }
  function identity(item) {
    // Line keys can change when discounts change. Properties distinguish repeated variant IDs.
    return JSON.stringify([String(item.variant_id || item.id),
      Object.entries(item.properties || {}).sort(([a],[b]) => a.localeCompare(b))]);
  }
  function locate(cart, signature) {
    const matches = cart.items.filter(item => identity(item) === signature);
    if (matches.length > 1) throw new Error('This configuration is ambiguous. Refresh the cart.');
    return matches[0];
  }
  function visibleCount(cart) {
    return cart.items.reduce((sum, item) => {
      const hidden = groupId(item) && parentKey(item) && cart.items.some(parent =>
        parent.key === parentKey(item) && groupId(parent) === groupId(item));
      return sum + (hidden ? 0 : item.quantity);
    }, 0);
  }
  function totals(cart, id) {
    return members(cart, id).reduce((sum, item) => ({
      original: sum.original + item.original_line_price,
      final: sum.final + item.final_line_price
    }), {original:0, final:0});
  }
  async function changeGroup(initial, id, quantity, api) {
    if (!Number.isSafeInteger(quantity) || quantity < 0) throw new Error('Please enter a valid quantity.');
    const original = members(initial, id);
    const snapshots = original.map(item => ({signature:identity(item), quantity:item.quantity}));
    let cart = initial;
    try {
      // change.js validates stock; update.js can skip inventory checks for existing lines.
      // Re-resolve keys after every response, including after discounts change line keys.
      for (const saved of snapshots) {
        const current = locate(cart, saved.signature);
        if (!current) {
          if (quantity === 0) continue; // removing the parent can automatically remove children
          throw new Error('A selected option is no longer in your cart. Please remove this product and configure it again.');
        }
        if (current.quantity !== quantity) cart = await api.change(current.key, quantity);
      }
      if (quantity === 0) {
        if (snapshots.some(saved => locate(cart,saved.signature))) throw new Error('The complete configuration could not be removed.');
      } else {
        for (const saved of snapshots) {
          if (locate(cart,saved.signature)?.quantity !== quantity) throw new Error('The requested quantity is not available for every selected option.');
        }
      }
      return cart;
    } catch (cause) {
      // Read authoritative state before recovering a partial/uncertain multi-line change.
      let recovery = false;
      try {
        cart = await api.read();
        if (quantity !== 0) {
          for (const saved of snapshots) {
            const current = locate(cart,saved.signature);
            if (!current) throw new Error('A configuration line was removed.');
            if (current.quantity !== saved.quantity) cart = await api.change(current.key,saved.quantity);
          }
          recovery = snapshots.every(saved => locate(cart,saved.signature)?.quantity === saved.quantity);
        } else {
          recovery = snapshots.every(saved => !locate(cart,saved.signature));
          if (recovery) return cart; // removal committed even if its response was lost
        }
      } catch (ignored) { /* Never claim a rollback succeeded without verifying it. */ }
      const error = new Error((cause.message || 'Unable to update the selected options.') +
        (recovery ? ' Your previous quantities have been restored.' : ' Please refresh and review the cart before checkout.'));
      error.cart = cart;
      error.recovered = recovery;
      throw error;
    }
  }
  const core = {groupId,parentKey,members,identity,locate,visibleCount,totals,changeGroup};
  if (typeof module !== 'undefined' && module.exports) module.exports = core;
  if (typeof window === 'undefined' || !window.customElements || window.cpfCartGroupsInstalled) return;
  window.cpfCartGroupsInstalled = true;
  let busy = false;
  const unresolved = new Set();
  const root = () => window.Shopify?.routes?.root || '/';
  const rows = host => Array.from(host.querySelectorAll('[data-cart-item][data-cpf-line-key]'));
  const hosts = () => Array.from(document.querySelectorAll('m-cart, m-cart-drawer'));
  function visibleDOMCount(host, fallback) {
    const list = rows(host);
    if (!list.some(row => row.hasAttribute('data-cpf-group-parent'))) return fallback;
    return list.reduce((sum,row) => sum + (row.hasAttribute('data-cpf-group-child') ? 0 : Number(row.dataset.cpfQuantity || 0)),0);
  }
  function countBubbles(count) {
    document.querySelectorAll('.m-cart-count-bubble').forEach(bubble => {
      bubble.textContent = String(count);
      bubble.classList.toggle('m:hidden',count === 0);
      bubble.classList.toggle('hidden',count === 0);
    });
  }
  // Use the cart response associated with each theme count update. Drawer markup
  // can still be refreshing when getCart() resolves.
  const countCarts = new WeakMap();
  function installCartCount(proto) {
    const originalGetCart = proto.getCart;
    proto.getCart = async function(...args) {
      const cart = await originalGetCart.apply(this,args);
      countCarts.set(this,cart);
      return cart;
    };
    proto.updateCartCount = function(count) {
      const cart = countCarts.get(this);
      countCarts.delete(this);
      const host = this.closest('m-cart-drawer') || this;
      countBubbles(Array.isArray(cart?.items) ? visibleCount(cart) : visibleDOMCount(host,count));
    };
  }
  function message(host, text, id) {
    let target = rows(host).find(row => row.dataset.cpfGroup === id && row.hasAttribute('data-cpf-group-parent'));
    target = target?.querySelector('.m-cart-item__info') || host;
    let note = target.querySelector('[data-cpf-cart-message]');
    if (!note) {
      note = document.createElement('p');
      note.setAttribute('data-cpf-cart-message','');
      note.setAttribute('role','alert');
      note.style.cssText = 'color:#a40000;font-size:14px;margin:12px 0;';
      target.append(note);
    }
    note.textContent = text;
  }
  const api = {
    async read() {
      const response = await fetch(root()+'cart.js',{headers:{Accept:'application/json'},cache:'no-store'});
      if (!response.ok) throw new Error('Unable to refresh your cart.');
      return response.json();
    },
    async change(key,quantity) {
      const response = await fetch(root()+'cart/change.js',{
        method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},
        body:JSON.stringify({id:key,quantity})
      });
      const cart = await response.json();
      if (!response.ok || cart.errors || !Array.isArray(cart.items)) {
        throw new Error(typeof cart.description === 'string' ? cart.description :
          typeof cart.errors === 'string' ? cart.errors : 'The requested quantity is not available.');
      }
      return cart;
    }
  };
  async function refresh(cart) {
    const present = hosts();
    const drawers = present.filter(host => host.tagName.toLowerCase() === 'm-cart-drawer');
    const pages = present.filter(host => host.tagName.toLowerCase() === 'm-cart');
    const replacements = [];
    // Render the drawer in the current page/preview context. Keep its outer
    // element, close button and inner panel alive so their listeners stay bound.
    for (const drawer of drawers) {
      const url = new URL(window.location.href);
      url.searchParams.delete('sections');
      url.searchParams.set('section_id','cart-drawer');
      const response = await fetch(url.toString(),{cache:'no-store'});
      if (!response.ok) throw new Error('Unable to refresh cart markup.');
      const parsed = new DOMParser().parseFromString(await response.text(),'text/html');
      const source = parsed.querySelector('m-cart-drawer');
      if (!source) throw new Error('Cart drawer section is unavailable.');
      for (const selector of ['[data-minimog-cart-items]', '[data-minimog-cart-discounts]', '[data-cart-subtotal]', '[data-minimog-gift-wrapping]']) {
        const target = drawer.querySelector(selector);
        const fresh = source.querySelector(selector);
        if (selector === '[data-minimog-cart-items]' && (!target || !fresh)) throw new Error('Cart drawer items are unavailable.');
        if (target) replacements.push({target,html:fresh?.innerHTML || ''});
      }
    }
    if (pages.length) {
      const sectionNames = ['cart-template'];
      const response = await fetch(root()+'cart?sections='+sectionNames.join(','),{headers:{Accept:'application/json'},cache:'no-store'});
      if (!response.ok) throw new Error('Unable to refresh cart markup.');
      const sections = await response.json();
      for (const host of pages) {
        const section = host.tagName.toLowerCase() === 'm-cart' ? 'cart-template' : 'cart-drawer';
        if (typeof sections[section] !== 'string') throw new Error('Cart section is unavailable.');
        const parsed = new DOMParser().parseFromString(sections[section],'text/html');
        // Swap each host's *entire* contents from the freshly rendered section, rather than
        // grafting a fixed list of child selectors. The theme's empty-cart state (or the
        // transition back into a non-empty state) can legitimately omit any of those
        // selectors -- e.g. gift-wrapping/discounts blocks are typically wrapped in
        // {% if cart.item_count > 0 %} -- so requiring all of them to exist made removing
        // the last item throw and abort the whole refresh before any DOM was patched.
        const sourceRoot = parsed.body.firstElementChild;
        if (!sourceRoot) throw new Error('Cart section is empty.');
        replacements.push({target: host, html: sourceRoot.innerHTML});
      }
    }
    replacements.forEach(({target,html}) => {target.innerHTML=html;});
    present.forEach(host => host.classList.toggle('m-cart--empty',cart.item_count === 0));
    countBubbles(visibleCount(cart));
    try {
      window.MinimogEvents?.emit(window.MinimogTheme.pubSubEvents.cartUpdate,{...cart,source:'main-cart-items'});
      window.FoxKitV2?.Modules?.InCart?.getCart();
    } catch (ignored) { /* Rendering succeeded; unrelated integrations must not retry mutations. */ }
  }
  function invalidGroups() {
    return unresolved.size || document.querySelector('[data-cpf-group-invalid]');
  }
  const acceleratedState = new WeakMap();
  function syncAcceleratedLock() {
    const locked = busy || !!invalidGroups();
    document.querySelectorAll('m-cart .additional-checkout-buttons,m-cart-drawer .additional-checkout-buttons').forEach(node => {
      if (locked) {
        if (!acceleratedState.has(node)) acceleratedState.set(node,node.inert);
        node.inert=true;
      } else if (acceleratedState.has(node)) {
        node.inert=acceleratedState.get(node);
        acceleratedState.delete(node);
      }
    });
  }
  function handleSubmit(event) {
    if (!event.target.matches('form[action*="/cart"]')) return;
    if (!busy && !invalidGroups()) return;
    event.preventDefault();event.stopImmediatePropagation();
    message(event.target,busy ? 'Please wait while your product options update.' : 'Please update or remove the product with mismatched option quantities before checkout.');
  }
  document.addEventListener('submit',handleSubmit,true);
  document.addEventListener('click',event => {
    const host = event.target.closest('m-cart,m-cart-drawer');
    if (!host) return;
    const checkout = event.target.closest('[name="checkout"],a[href*="/checkout"],.additional-checkout-buttons');
    if (busy || (checkout && invalidGroups())) {
      // The close icon stays usable. Prevent another quantity/remove/checkout action while changing a group.
      if (!event.target.closest('.m-cart-drawer__close')) {
        event.preventDefault();event.stopImmediatePropagation();
        if (!busy) message(host,'Please update or remove the product with mismatched option quantities before checkout.');
      }
    }
  },true);

  customElements.whenDefined('m-cart').then(() => {
    const proto = customElements.get('m-cart').prototype;
    const originalUpdate = proto.updateQuantity;
    installCartCount(proto);
    proto.updateQuantity = async function(line,rawQuantity,name) {
      const row = rows(this).find(node => Number(node.dataset.index) === Number(line));
      if (busy) {
        row?.querySelectorAll('input[name="updates[]"]').forEach(input => {input.value=input.defaultValue;});
        return;
      }
      if (!row?.hasAttribute('data-cpf-group-parent') && !row?.hasAttribute('data-cpf-group-child')) {
        return originalUpdate.call(this,line,rawQuantity,name);
      }
      const id = row.dataset.cpfGroup;
      const quantity = Number(rawQuantity);
      const quantityInput = row.querySelector('input[name="updates[]"]');
      if (String(rawQuantity).trim() === '' || !Number.isSafeInteger(quantity) || quantity < 0 || (quantity > 0 && quantityInput && !quantityInput.checkValidity())) {
        message(this,'Please enter an allowed quantity.',id);
        if (quantityInput) quantityInput.value=quantityInput.defaultValue;
        return;
      }
      busy=true;
      syncAcceleratedLock();
      this.loading?.start();
      let cart,error;
      try {
        cart = await api.read();
        cart = await changeGroup(cart,id,quantity,api);
        unresolved.delete(id);
      } catch (failure) {
        error=failure;
        if (!failure.recovered) unresolved.add(id);
        try {cart=await api.read();} catch (ignored) {cart=failure.cart;}
      }
      try {
        if (!cart) throw new Error('Cart state unavailable.');
        await refresh(cart);
        if (error) message(this,error.message,id);
        else if (quantity>0) rows(this).find(node => node.dataset.cpfGroup===id && node.hasAttribute('data-cpf-group-parent'))?.querySelector('input[name="updates[]"]')?.focus();
      } catch (renderError) {
        // TEMP DIAGNOSTIC: log the real cause before it's masked by the redirect below.
        // Check the console + the Network tab's cart?sections=... request/response when
        // this fires, then remove this console.error once the cause is confirmed.
        console.error('[cpf-cart] refresh() failed:', renderError, {host: this.tagName, line, quantity});
        // Never repeat a successful cart change because its drawer refresh failed.
        window.location.assign(root()+'cart');
      } finally {
        this.loading?.finish();busy=false;syncAcceleratedLock();
      }
    };
  });
  customElements.whenDefined('m-cart-drawer').then(() => {
    const proto=customElements.get('m-cart-drawer').prototype;
    installCartCount(proto);
  });
  // Correct the initial badge when a page is loaded with configured items already in the cart.
  const initialHost=hosts().find(host => rows(host).some(row => row.hasAttribute('data-cpf-group-parent')));
  if (initialHost) countBubbles(visibleDOMCount(initialHost,0));
  syncAcceleratedLock();
})();